export const SIZE_CHART_UNITS = Object.freeze(["mm", "cm", "in"]);

export function normalizeSizeChartUnit(rawUnit) {
  const u = String(rawUnit || "").trim().toLowerCase();
  return u === "mm" || u === "in" ? u : "cm";
}

export function isConvertibleCell(cell) {
  const s = String(cell ?? "").trim();
  if (!s) return false;
  const normalized = s.replace(/,/g, "");
  if (/[^0-9.\-–—\s]/.test(normalized)) return false;
  if (/^-?\d+(?:\.\d+)?$/.test(normalized)) return true;
  if (/^(-?\d+(?:\.\d+)?)[\s]*(?:-|–|—)[\s]*(-?\d+(?:\.\d+)?)$/.test(normalized)) return true;
  return false;
}

export function formatConvertedNumber(value, unit) {
  const u = normalizeSizeChartUnit(unit);
  if (!Number.isFinite(value)) return "";
  const decimals = u === "in" ? 2 : u === "cm" ? 1 : 0;
  const fixed = value.toFixed(decimals);
  return fixed.replace(/\.0+$/, "").replace(/(\.\d*?)0+$/, "$1");
}

export function convertValueBetweenUnits(value, fromUnit, toUnit) {
  const from = normalizeSizeChartUnit(fromUnit);
  const to = normalizeSizeChartUnit(toUnit);
  if (!Number.isFinite(value) || from === to) return value;
  const factors = {
    mm: { mm: 1, cm: 0.1, in: 1 / 25.4 },
    cm: { mm: 10, cm: 1, in: 1 / 2.54 },
    in: { mm: 25.4, cm: 2.54, in: 1 },
  };
  const factor = factors[from]?.[to] ?? 1;
  return value * factor;
}

function parseNumericOrRange(cell) {
  const s = String(cell ?? "").trim();
  if (!s) return null;
  const normalized = s.replace(/,/g, "");
  const single = normalized.match(/^-?\d+(?:\.\d+)?$/);
  if (single) return { kind: "single", a: Number(single[0]) };
  const range = normalized.match(/^(-?\d+(?:\.\d+)?)[\s]*(?:-|–|—)[\s]*(-?\d+(?:\.\d+)?)$/);
  if (range) return { kind: "range", a: Number(range[1]), b: Number(range[2]) };
  return null;
}

export function convertDisplayRows(rows, fromUnit, toUnit) {
  const from = normalizeSizeChartUnit(fromUnit);
  const to = normalizeSizeChartUnit(toUnit);
  if (!Array.isArray(rows)) return [];
  if (from === to) return rows;
  return rows.map((row) => {
    const r = Array.isArray(row) ? row : [];
    return r.map((cell) => {
      const parsed = parseNumericOrRange(cell);
      if (!parsed) return cell;
      if (parsed.kind === "single") {
        return formatConvertedNumber(convertValueBetweenUnits(parsed.a, from, to), to);
      }
      const a = formatConvertedNumber(convertValueBetweenUnits(parsed.a, from, to), to);
      const b = formatConvertedNumber(convertValueBetweenUnits(parsed.b, from, to), to);
      return `${a} - ${b}`;
    });
  });
}

export function hasValidSizeChart(sizeChart) {
  if (!sizeChart || typeof sizeChart !== "object") return false;
  const columns = Array.isArray(sizeChart.columns) ? sizeChart.columns : [];
  const rows = Array.isArray(sizeChart.rows) ? sizeChart.rows : [];
  if (columns.length <= 0) return false;
  if (rows.length <= 0) return false;
  const hasAnyCell = rows.some(
    (r) => Array.isArray(r) && r.some((c) => String(c ?? "").trim().length > 0)
  );
  return hasAnyCell;
}

